import React, { useState, useEffect } from 'react';
import toastService from '../services/toastService';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import SearchResultCard from './SearchResultCard';
import useMutualInterests from '../utils/useMutualInterests';

import MessageModal from './MessageModal';
import PIIRequestModal from './PIIRequestModal';
import ChatFirstPrompt from './ChatFirstPrompt';
import logger from '../utils/logger';
import './SearchPage2.css';

const Shortlist = () => {
  const [shortlist, setShortlist] = useState([]);
  const { theirFav, theirShort } = useMutualInterests(localStorage.getItem('username'));
  const isMutualUser = (user) => theirShort.has(user.username) || (user.isFavorited && theirFav.has(user.username));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [piiRequests, setPiiRequests] = useState({});
  const [piiAccessMap, setPiiAccessMap] = useState({});
  const [piiRequestsMap, setPiiRequestsMap] = useState({});
  
  // Message modal state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // PII Request modal state
  const [showPIIRequestModal, setShowPIIRequestModal] = useState(false);
  const [selectedUserForPII, setSelectedUserForPII] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  
  // Chat-first prompt state
  const [showChatFirstPrompt, setShowChatFirstPrompt] = useState(false);
  const [pendingPIIRequestUser, setPendingPIIRequestUser] = useState(null);
  
  const navigate = useNavigate();
  const currentUsername = localStorage.getItem('username');

  const [cardsPerRow, setCardsPerRow] = useState(() => {
    const raw = localStorage.getItem('searchCardsPerRow');
    const parsed = raw ? parseInt(raw, 10) : 3;
    return [2, 3, 4, 5, 6].includes(parsed) ? parsed : 3;
  });

  useEffect(() => {
    loadShortlist();
    loadPiiRequests();
    loadCurrentUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadShortlist = async () => {
    try {
      const username = localStorage.getItem('username');
      if (!username) {
        navigate('/login');
        return;
      }

      const response = await api.get(`/shortlist/${username}`);
      setShortlist(response.data.shortlist || []);
    } catch (err) {
      logger.error('Error loading shortlist:', err);
      setError('Failed to load shortlist');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentUserProfile = async () => {
    try {
      const username = localStorage.getItem('username');
      if (!username) return;
      const response = await api.get(`/profile/${username}?requester=${username}`);
      setCurrentUserProfile(response.data);
    } catch (err) {
      logger.error('Error loading current user profile:', err);
    }
  };

  const loadPiiRequests = async () => {
    const currentUser = localStorage.getItem('username');
    if (!currentUser) return;

    try {
      const [requestsResponse, accessResponse] = await Promise.all([
        api.get(`/pii-requests/${currentUser}/outgoing`),
        api.get(`/pii-access/${currentUser}/received`)
      ]);

      const requests = requestsResponse.data.requests || [];
      const receivedAccess = accessResponse.data.receivedAccess || accessResponse.data.access || [];
      const requestStatus = {};
      const piiMap = {};
      const piiReqMap = {};

      // Track detailed request status
      requests.forEach(req => {
        const targetUsername = req.profileUsername || req.requestedUsername || req.profileOwner?.username;
        if (targetUsername && req.requestType) {
          if (!piiReqMap[targetUsername]) {
            piiReqMap[targetUsername] = {};
          }
          piiReqMap[targetUsername][req.requestType] = req.status;
          requestStatus[`${targetUsername}_${req.requestType}`] = req.status;
        }
      });

      // Track approved access
      receivedAccess.forEach(access => {
        const targetUsername = access.userProfile?.username;
        if (targetUsername) {
          piiMap[targetUsername] = {
            hasContactAccess: access.accessTypes?.includes('contact_info'),
            hasImageAccess: access.accessTypes?.includes('images'),
            hasDateOfBirthAccess: access.accessTypes?.includes('date_of_birth'),
            hasLinkedInAccess: access.accessTypes?.includes('linkedin_url')
          };
          access.accessTypes?.forEach(accessType => {
            requestStatus[`${targetUsername}_${accessType}`] = 'approved';
            if (!piiReqMap[targetUsername]) {
              piiReqMap[targetUsername] = {};
            }
            piiReqMap[targetUsername][accessType] = 'approved';
          });
        }
      });

      setPiiRequests(requestStatus);
      setPiiAccessMap(piiMap);
      setPiiRequestsMap(piiReqMap);
    } catch (err) {
      logger.error('Error loading PII requests:', err);
    }
  };

  const hasPiiAccess = (targetUsername) => {
    return piiRequests[`${targetUsername}_contact_info`] === 'approved';
  };

  const hasImageAccess = (targetUsername) => {
    return piiRequests[`${targetUsername}_images`] === 'approved';
  };

  const isPiiRequestPending = (targetUsername) => {
    return piiRequests[`${targetUsername}_contact_info`] === 'pending';
  };

  const isImageRequestPending = (targetUsername) => {
    return piiRequests[`${targetUsername}_images`] === 'pending';
  };

  // Show chat-first prompt before PII request
  const handlePIIRequest = (user) => {
    setPendingPIIRequestUser(user);
    setShowChatFirstPrompt(true);
  };

  // Actually open the PII modal (after chat-first prompt)
  const actuallyOpenPIIModal = async (user) => {
    try {
      // Fetch target user's full profile to get accurate visibility settings
      const profileResponse = await api.get(`/profile/${user.username}`);
      const targetProfile = profileResponse.data;
      
      setSelectedUserForPII({
        ...user,
        contactNumberVisible: targetProfile.contactNumberVisible,
        contactEmailVisible: targetProfile.contactEmailVisible,
        linkedinUrlVisible: targetProfile.linkedinUrlVisible,
        imageVisibility: targetProfile.imageVisibility
      });
      setShowPIIRequestModal(true);
    } catch (err) {
      logger.error('Failed to fetch profile for PII modal:', err);
      setSelectedUserForPII(user);
      setShowPIIRequestModal(true);
    }
  };

  const handlePIIRequestSuccess = async () => {
    await loadPiiRequests();
    setShowPIIRequestModal(false);
    setSelectedUserForPII(null);
  };

  const getPIIRequestStatus = (username) => {
    const userRequests = piiRequestsMap[username] || {};
    const userAccess = piiAccessMap[username] || {};
    
    return {
      images: userRequests.images || (userAccess.hasImageAccess ? 'approved' : null),
      contact_info: userRequests.contact_info || (userAccess.hasContactAccess ? 'approved' : null),
      date_of_birth: userRequests.date_of_birth || (userAccess.hasDateOfBirthAccess ? 'approved' : null),
      linkedin_url: userRequests.linkedin_url || (userAccess.hasLinkedInAccess ? 'approved' : null)
    };
  };

  const removeFromShortlist = async (user) => {
    console.log('removeFromShortlist called for:', user.username);
    try {
      const username = localStorage.getItem('username');
      console.log('Calling API delete for:', user.username, 'by user:', username);
      await api.delete(`/shortlist/${user.username}?username=${encodeURIComponent(username)}`);
      console.log('API delete successful');
      setShortlist(shortlist.filter(item => item.username !== user.username));
      setStatusMessage('✅ Removed from shortlist!');
      setTimeout(() => setStatusMessage(''), 3000);
    } catch (err) {
      console.error('Error removing from shortlist:', err);
      logger.error('Error removing from shortlist:', err);
      setError('Failed to remove from shortlist');
      setStatusMessage('❌ Failed to remove from shortlist');
      setTimeout(() => setStatusMessage(''), 3000);
    }
  };

  const toggleFavorite = async (user) => {
    console.log('toggleFavorite called for:', user.username, 'isFavorited:', user.isFavorited);
    try {
      const username = localStorage.getItem('username');
      console.log('Current user:', username);
      // Check if already favorited by checking the user object
      const isFav = user.isFavorited || false;
      console.log('Is favorited:', isFav);

      if (isFav) {
        // Remove from favorites
        console.log('Removing from favorites...');
        await api.delete(`/favorites/${user.username}?username=${encodeURIComponent(username)}`);
        setStatusMessage('⭐ Removed from favorites!');
        // Update local state
        setShortlist(prev => prev.map(item =>
          item.username === user.username ? { ...item, isFavorited: false } : item
        ));
      } else {
        // Add to favorites
        console.log('Adding to favorites...');
        const res = await api.post(`/favorites/${user.username}?username=${encodeURIComponent(username)}`);
      if (res?.data?.mutual) toastService.success('💞 Mutual favorites — they favorited you back!');
        setStatusMessage('⭐ Added to favorites!');
        // Update local state
        setShortlist(prev => prev.map(item =>
          item.username === user.username ? { ...item, isFavorited: true } : item
        ));
      }
      setTimeout(() => setStatusMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling favorite:', err);
      logger.error('Error toggling favorite:', err);
      setStatusMessage('❌ Failed to toggle favorite');
      setTimeout(() => setStatusMessage(''), 3000);
    }
  };

  const handleMessage = async (user) => {
    // If user object doesn't have full profile data, fetch it
    if (!user.firstName && !user.location && user.username) {
      try {
        const response = await api.get(`/profile/${user.username}?requester=${currentUsername}`);
        setSelectedUser(response.data);
      } catch (err) {
        logger.error('Error loading user profile:', err);
        // Fallback to existing user object
        setSelectedUser(user);
      }
    } else {
      setSelectedUser(user);
    }
    setShowMessageModal(true);
  };

  if (loading) return <div className="text-center py-4"><div className="spinner-border"></div></div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="search-page">
      <div className="container-fluid">
        <h2 className="mb-4">📋 My Shortlist</h2>

        {shortlist.length > 0 && (
          <div className="results-controls-top-centered">
            <div className="cards-per-row-selector">
              <span className="selector-label">Cards:</span>
              {[2, 3, 4, 5, 6].map(num => (
                <button
                  key={num}
                  className={`cards-btn ${cardsPerRow === num ? 'active' : ''}`}
                  onClick={() => {
                    setCardsPerRow(num);
                    localStorage.setItem('searchCardsPerRow', num.toString());
                  }}
                  title={`${num} cards per row`}
                  type="button"
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}

        {statusMessage && (
          <div className={`alert ${statusMessage.includes('❌') ? 'alert-danger' : 'alert-success'} alert-dismissible fade show`} role="alert">
            {statusMessage}
            <button type="button" className="btn-close" onClick={() => setStatusMessage('')}></button>
          </div>
        )}

        {shortlist.length === 0 ? (
          <div className="alert alert-info">
            <p className="mb-0">No profiles in shortlist yet. Add profiles you're considering!</p>
          </div>
        ) : (
          <div className={`results-grid results-grid-centered cards-per-row-${cardsPerRow}`}>
            {shortlist.map(user => (
              <SearchResultCard
                key={user.username}
                user={user}
                context="my-shortlists"
                currentUsername={localStorage.getItem('username')}
                onRemove={removeFromShortlist}
                onToggleShortlist={removeFromShortlist}
                onToggleFavorite={toggleFavorite}
                onMessage={handleMessage}
                onPIIRequest={handlePIIRequest}
                isShortlisted={true}
                isFavorited={user.isFavorited || false}
                isMutual={isMutualUser(user)}
                hasPiiAccess={hasPiiAccess(user.username)}
                hasImageAccess={hasImageAccess(user.username)}
                isPiiRequestPending={isPiiRequestPending(user.username)}
                isImageRequestPending={isImageRequestPending(user.username)}
                piiRequestStatus={getPIIRequestStatus(user.username)}
                showFavoriteButton={true}
                showShortlistButton={false}
                showExcludeButton={false}
                showRemoveButton={true}
                removeButtonLabel="Remove from Shortlist"
                removeButtonIcon="📋"
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Message Modal */}
      {showMessageModal && selectedUser && (
        <MessageModal
          isOpen={showMessageModal}
          profile={selectedUser}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Chat First Prompt */}
      <ChatFirstPrompt
        isOpen={showChatFirstPrompt}
        onClose={() => {
          setShowChatFirstPrompt(false);
          setPendingPIIRequestUser(null);
        }}
        onContinue={() => {
          if (pendingPIIRequestUser) {
            actuallyOpenPIIModal(pendingPIIRequestUser);
          }
          setPendingPIIRequestUser(null);
        }}
        onOpenChat={() => {
          if (pendingPIIRequestUser) {
            handleMessage(pendingPIIRequestUser);
          }
          setPendingPIIRequestUser(null);
        }}
        targetUser={pendingPIIRequestUser}
      />

      {/* PII Request Modal */}
      {showPIIRequestModal && selectedUserForPII && (
        <PIIRequestModal
          isOpen={showPIIRequestModal}
          profileUsername={selectedUserForPII.username}
          profileName={`${selectedUserForPII.firstName || selectedUserForPII.username}`}
          visibilitySettings={{
            contactNumberVisible: selectedUserForPII.contactNumberVisible,
            contactEmailVisible: selectedUserForPII.contactEmailVisible,
            linkedinUrlVisible: selectedUserForPII.linkedinUrlVisible
          }}
          targetProfile={selectedUserForPII}
          requesterProfile={currentUserProfile}
          onClose={() => {
            setShowPIIRequestModal(false);
            setSelectedUserForPII(null);
          }}
          onSuccess={handlePIIRequestSuccess}
          currentAccess={piiAccessMap[selectedUserForPII.username] || {}}
          requestStatus={getPIIRequestStatus(selectedUserForPII.username)}
        />
      )}
    </div>
  );
};

export default Shortlist;
